import { AppropriatenessTestDto } from 'api/Clients/Types/AppropriatenessTestDto'
import { AuditLog } from 'api/Clients/Types/AuditLog'
import { BusinessNature } from 'api/Clients/Types/BusinessNature'
import { ClientDto } from 'api/Clients/Types/Client'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { ClientDuplicateDto } from 'api/Clients/Types/ClientDuplicateDto'
import { ClientLeverageType } from 'api/Clients/Types/ClientLeverageType'
import { ClientNoteDto } from 'api/Clients/Types/ClientNoteDto'
import { ClientRelationsDto } from 'api/Clients/Types/ClientRelationDto'
import { ClientRestriction } from 'api/Clients/Types/ClientRestriction'
import { ClientScreeningCasesDto } from 'api/Clients/Types/ClientScreeningCasesDto'
import { ClientStatus } from 'api/Clients/Types/ClientStatus'
import { ClientStatusHistory } from 'api/Clients/Types/ClientStatusHistory'
import { ClientType } from 'api/Clients/Types/ClientType'
import { ClientVerification } from 'api/Clients/Types/ClientVerification'
import { ClientVerificationReportDetails } from 'api/Clients/Types/ClientVerificationReportDetails'
import { CountryComplianceDto } from 'api/Clients/Types/CountryComplianceDto'
import { CountryPreferenceDto } from 'api/Clients/Types/CountryPreference'
import {
  AppTestBuilderDto,
  AppTestDetailsDto,
  moveAnswersIntoQuestion,
} from 'api/Clients/Types/CreateClientAppTest'
import { CreateClientNote } from 'api/Clients/Types/CreateClientNote'
import { CreateClientTest } from 'api/Clients/Types/CreateClientTest'
import { CreateNciHistory } from 'api/Clients/Types/CreateNciHistory'
import { EduLevel } from 'api/Clients/Types/EduLevel'
import { Language } from 'api/Clients/Types/Language'
import { ClientLegalBasesTypes } from 'api/Clients/Types/LegalBasesTypes'
import {
  CreateTickmillCompanyLossPercentage,
  LossPercentageDto,
  UpdateTickmillCompanyLossPercentage,
} from 'api/Clients/Types/LossPercentageDto'
import { NCIHistory } from 'api/Clients/Types/NCIHistory'
import { NciPriorityDto } from 'api/Clients/Types/NciPriorityDto'
import { NciType } from 'api/Clients/Types/NciType'
import { RoleSecuredProperties } from 'api/Clients/Types/RoleSecuredProperties'
import { SalesStatus } from 'api/Clients/Types/SalesStatus'
import { SecuredPropertiesLabels } from 'api/Clients/Types/SecuredPropertiesLabels'
import { StatusReason } from 'api/Clients/Types/StatusReason'
import { TestAnswerDto, TestDto } from 'api/Clients/Types/TestDto'
import { TestSectionsDto } from 'api/Clients/Types/TestSectionsDto'
import { TickmillProductDto } from 'api/Clients/Types/TickmillProductDto'
import { TruliooFieldDto } from 'api/Clients/Types/TruliooFieldDto'
import { TruliooStatus } from 'api/Clients/Types/TruliooStatus'
import { TruliooVerification } from 'api/Clients/Types/TruliooVerification'
import { UpdateClientNote } from 'api/Clients/Types/UpdateClientNote'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { UpdateCompanyDetailsDto } from 'api/Clients/Types/UpdateCompanyDetailsDto'
import { UpdateCountryPreference } from 'api/Clients/Types/UpdateCountryPreference'
import { WorldCheckStatus } from 'api/Clients/Types/WorldCheckStatus'
import { NameDto } from 'api/Common/Types'
import { DocumentTypeDto } from 'api/Documents/Types/DocumentTypeDto'
import { PageData } from 'api/Pagination/Types'
import { AddRolePermissionFormValues } from 'routes/Administration/Roles/EditRolePermissionForm'
import { AppropriatnessTestScoreSetting } from 'routes/Compliance/AppropriatenessTest/interface'
import { AddEditCountryRiskValues } from 'routes/Compliance/CountryRisk/AddEditCountryRiskForm'
import { Affiliate } from 'routes/Records/Clients/Details/SelectAffiliate'

import { AMLAdjustmentReason } from '../../api/Clients/Types/AMLAdjustmentReason'
import { AmlValueDetailedDto } from '../../api/Clients/Types/AmlValueDetailedDto'
import { ClientAMLDetails } from '../../api/Clients/Types/ClientAMLDetails'
import {
  ToggleExcludeFromBonusCampaignRestriction,
  ToggleExcludeLuckyDraw,
  ToggleInterestRatesRestriction,
} from '../../api/Clients/Types/ClientRestrictions'
import { CountriesProductDto } from '../../api/Clients/Types/CountriesProduct'
import { CreateAMLAdjustment } from '../../api/Clients/Types/CreateAMLAdjustment'
import { LandingPageDto } from '../../api/Clients/Types/LandingPageDto'
import { UpdateClientOther } from '../../api/Clients/Types/UpdateClientOther'
import { UpdateCountryProduct } from '../../api/Clients/Types/UpdateCountryProduct'
import { UpdateIndividualDetailsDto } from '../../api/Clients/Types/UpdateIndividualDetailsDto'
import { UpdateMultiTierRegistrationAllowedFlag } from '../../api/Clients/Types/UpdateMultiTierRegistrationAllowedFlag'
import { CountryPreference } from '../../routes/Administration/Countries/types'
import { ApiClient } from './ApiClient'
import { PageQuery, preparePageQueryParams } from './index'
import { createLanguageQueryString } from './queryStringUtils'

export class ClientsApiClient {
  constructor(private apiClient: ApiClient) {}

  getClients: (query?: PageQuery) => Promise<PageData<ClientDto[]>> = (query) => {
    return this.apiClient.get(`clients/clients?${preparePageQueryParams(query)}`)
  }
  getClient: (id: string) => Promise<ClientDetailedDto> = (id) => {
    return this.apiClient.get(`clients/clients/${id}`)
  }
  getClientBasicDto: (id: string) => Promise<ClientDto> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/basic`)
  }
  updateClientIndividualDetails: (
    data: UpdateIndividualDetailsDto,
    ignoreCheck: boolean
  ) => Promise<ClientDetailedDto> = (data, ignoreCheck) => {
    return this.apiClient.put(
      `clients/clients/${data.id}/individual-details/?ignoreCheck=${ignoreCheck}`,
      data
    )
  }
  updateClientCorporateDetails: (
    data: UpdateCompanyDetailsDto,
    ignoreCheck: boolean
  ) => Promise<ClientDetailedDto> = (data, ignoreCheck) => {
    return this.apiClient.put(
      `clients/clients/${data.id}/company-details?ignoreCheck=${ignoreCheck}`,
      data
    )
  }
  updateOtherOptions: (id: string, data: UpdateClientOther) => Promise<void> = (id, data) => {
    return this.apiClient.put(`clients/clients/${id}/other`, data)
  }
  updateClientStatus: (data: UpdateClientStatus) => Promise<ClientDetailedDto> = (data) => {
    return this.apiClient.put(`clients/clients/${data.id}/status`, data)
  }
  // TODO MOVE TO CLIENT AREA API!
  updateClientPOIRequest: (
    clientId: string,
    type:
      | 'backside-poi'
      | 'frontside-poi'
      | 'additional-poi'
      | 'address-clarification'
      | 'additional-poi-generic'
      | 'us-citizen-classification'
      | 'belgium-email'
      | 'additional-national-id'
      | 'additional-passport'
      | 'additional-residence-permit'
      | 'national-insurance-number'
  ) => Promise<void> = (clientId, type) => {
    return this.apiClient.post(`clientarea/clients/${clientId}/requests/${type}`, {})
  }
  sendClientPOARequest: (clientId: string, data: { tickmillCompanyId: number }) => Promise<void> = (
    clientId,
    data
  ) => {
    return this.apiClient.post(`clientarea/clients/${clientId}/requests/poa`, data)
  }
  sendClientPOAChinaRequest: (clientId: string) => Promise<void> = (clientId) => {
    return this.apiClient.post(`clientarea/clients/${clientId}/requests/other/ChinesePoa`, {})
  }
  sendBankStatementNeededCCCoveredIBRequest: (clientId: string) => Promise<void> = (clientId) => {
    return this.apiClient.post(
      `clientarea/clients/${clientId}/requests/transaction-documents/cc-covered-ib`,
      {}
    )
  }
  sendBankStatementNeededDepositConfirmationRequest: (clientId: string) => Promise<void> = (
    clientId
  ) => {
    return this.apiClient.post(
      `clientarea/clients/${clientId}/requests/transaction-documents/deposit-confirmation`,
      {}
    )
  }
  sendClientDrivingLicenseRequest: (clientId: string) => Promise<void> = (clientId) => {
    return this.apiClient.post(`clientarea/clients/${clientId}/requests/driving-license`, {})
  }
  sendClientProofOfDepositRequest: (clientId: string) => Promise<void> = (clientId) => {
    return this.apiClient.post(`/clientarea/clients/${clientId}/requests/proof-of-deposit`, {})
  }
  sendClientBankStatementRequest: (clientId: string) => Promise<void> = (clientId) => {
    return this.apiClient.post(`/clientarea/clients/${clientId}/requests/bank-statement`, {})
  }
  getClientNotes: (id: string) => Promise<ClientNoteDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/notes`)
  }
  // TODO missing type probably
  createClientNote: (data: CreateClientNote) => Promise<void> = (data) => {
    return this.apiClient.post(`clients/clients/notes`, data)
  }
  // TODO missing type probably
  updateClientNote: (data: UpdateClientNote) => Promise<void> = (data) => {
    return this.apiClient.put(`clients/clients/notes/${data.id}`, data)
  }
  removeClientNote: (id: string) => Promise<void> = (id) => {
    return this.apiClient.delete(`clients/clients/notes/${id}`)
  }
  getClientHistory: (id: string) => Promise<ClientStatusHistory> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/status`)
  }
  getClientLeverageType: (id: string, query?: PageQuery) => Promise<ClientLeverageType[]> = (
    id,
    query
  ) => {
    return this.apiClient.get(
      `clients/clients/${id}/default-leverage-types?${preparePageQueryParams(query)}`
    )
  }
  getClientPossibleDuplicate: (id: string) => Promise<ClientDuplicateDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/duplicates`)
  }
  getClientRestrictions: () => Promise<ClientRestriction[]> = () => {
    return this.apiClient.get(`clients/clients/restriction-types`)
  }
  getClientLogs: (clientId: string) => Promise<AuditLog[]> = (clientId) => {
    return this.apiClient.get(`clients/clients/${clientId}/events`)
  }
  getClientClassificationAnswers: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/classification_tests`)
  }
  getClassificationTestsOptions: () => Promise<{ id: string; name: string }[]> = () => {
    return this.apiClient.get(`clients/clients/classifications/ep-additional-info-required-types`)
  }
  getClientKYCTests: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/kycupdate_tests`)
  }
  getClientCorporateTests: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/corporate_tests`)
  }
  getClientTaxFormTests: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/tax_form_tests `)
  }
  getClientTests: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/aptests`)
  }
  getEconomicProfileTest: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/ep_tests`)
  }
  getClientTestAnswer: (testId: string) => Promise<TestAnswerDto> = (testId) => {
    return this.apiClient.get(`clients/clients/tests/${testId}`)
  }
  // TODO check type
  createClientTest: (data: CreateClientTest) => Promise<void> = (data) => {
    return this.apiClient.post(`clients/clients/${data.clientId}/test`, data)
  }
  createClientAppTest: (data: AppTestBuilderDto) => Promise<void> = (data) => {
    const payload = moveAnswersIntoQuestion(data)
    return this.apiClient.post('clients/tests/new-approach', payload)
  }
  setClientTestStatus: (testId: string, testStatusId: number) => Promise<void> = (
    testId,
    testStatusId
  ) => {
    return this.apiClient.put(`clients/clients/tests/${testId}/status`, { testStatusId })
  }
  allowRetakeClientTest: (testId: string) => Promise<void> = (testId) => {
    return this.apiClient.put(`clients/clients/tests/${testId}/allow-retake`, { allowRetake: true })
  }
  getClientAppTestQuestions: ({
    clientTestId,
    languageId,
  }: {
    clientTestId: string
    languageId: string
  }) => Promise<TestSectionsDto> = ({ clientTestId, languageId }) => {
    return this.apiClient.get(
      `clients/clients/tests/client_test_questions${createLanguageQueryString(languageId)}&clientTestId=${clientTestId}`
    )
  }
  getAffiliates: (query?: PageQuery) => Promise<PageData<Affiliate[]>> = (query) => {
    return this.apiClient.get(`clients/affiliates?${preparePageQueryParams(query)}`)
  }
  updateClientAffiliates: (data?: {
    clientId: string
    affiliateId: string
    ignoreChecks: boolean
  }) => Promise<void> = (data) => {
    return this.apiClient.post(`clients/affiliates/assign-affiliate-check`, data)
  }
  getClientAppTestQuestionsForTickmillCompany: ({
    tickmillCompanyId,
    version,
    subcategoryId,
    productId,
  }: {
    tickmillCompanyId: number
    subcategoryId?: string
    productId?: string
    version?: number
  }) => Promise<TestSectionsDto> = ({ tickmillCompanyId, version, subcategoryId, productId }) => {
    let url = `clients/clients/aptest_questions?tickmillCompanyId=${tickmillCompanyId}`
    if (version) {
      url += `&version=${version}`
    }
    if (subcategoryId) {
      url += `&SubcategoryId=${subcategoryId}`
    }
    if (productId) {
      url += `&ProductId=${productId}`
    }
    return this.apiClient.get(url)
  }
  getClientKYCTestQuestions: (tickmillCompanyId: number) => Promise<TestSectionsDto> = (
    tickmillCompanyId
  ) => {
    return this.apiClient.get(
      `clients/clients/kyc_update_questions?tickmillCompanyId=${tickmillCompanyId}`
    )
  }
  getClientCorporateTestQuestions: (
    tickmillCompanyId: number,
    subcategoryId: string
  ) => Promise<TestSectionsDto> = (tickmillCompanyId, subcategoryId) => {
    return this.apiClient.get(
      `clients//clients/corporate_test_questions?tickmillCompanyId=${tickmillCompanyId}&subcategoryId=${subcategoryId}`
    )
  }
  getClientClassificationTestQuestionsForTickmillCompany: (
    tickmillCompanyId: number,
    version?: number,
    subcategoryId?: string
  ) => Promise<TestSectionsDto> = (tickmillCompanyId, version, subcategoryId) => {
    let url = `clients/clients/classification_questions?tickmillCompanyId=${tickmillCompanyId}`
    if (version) {
      url += `&version=${version}`
    }
    if (subcategoryId) {
      url += `&subcategoryId=${subcategoryId}`
    }
    return this.apiClient.get(url)
  }

  getClientTaxTestQuestionsForTickmillCompany: (
    tickmillCompanyId: number,
    version?: number,
    subcategoryId?: string
  ) => Promise<TestSectionsDto> = (tickmillCompanyId, version, subcategoryId) => {
    let url = `clients/clients/tax_form_questions?tickmillCompanyId=${tickmillCompanyId}`
    if (version) {
      url += `&version=${version}`
    }
    if (subcategoryId) {
      url += `&subcategoryId=${subcategoryId}`
    }
    return this.apiClient.get(url)
  }

  getClientIbInfoTestQuestionsForTickmillCompany: (
    tickmillCompanyId: number,
    version?: number
  ) => Promise<TestSectionsDto> = (tickmillCompanyId, version) => {
    let url = `clients/clients/ibinfo_questions?tickmillCompanyId=${tickmillCompanyId}`
    if (version) {
      url += `&version=${version}`
    }
    return this.apiClient.get(url)
  }
  getClientIbInfoTestAnswers: (id: string) => Promise<TestDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/ibinfo_tests`)
  }
  getClientWorldCheckStatus: () => Promise<WorldCheckStatus[]> = () => {
    return this.apiClient.get(`clients/clients/world-check-statuses`)
  }
  getTaxAvailabilityStatus: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/tin-reasons`)
  }
  fetchAMLRiskCategories: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/aml/risk-categories`)
  }
  fetchAMLAdjustmentReasons: () => Promise<AMLAdjustmentReason[]> = () => {
    return this.apiClient.get(`clients/aml/adjustment-reasons`)
  }
  getOwnershipStructures: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/ownership-structures`)
  }
  getTickmillProducts: () => Promise<TickmillProductDto[]> = () => {
    return this.apiClient.get(`clients/clients/tickmill-products`)
  }
  getClientTruliooEidStatus: () => Promise<TruliooStatus[]> = () => {
    return this.apiClient.get(`clients/clients/trulioo-eid-statuses`)
  }
  getClientTruliooWatchlistStatus: () => Promise<TruliooStatus[]> = () => {
    return this.apiClient.get(`clients/clients/trulioo-watchlist-statuses`)
  }
  getClientTruliooDocVStatus: () => Promise<TruliooStatus[]> = () => {
    return this.apiClient.get(`clients/clients/trulioo-docv-statuses`)
  }
  getClientSalesStatus: () => Promise<SalesStatus[]> = () => {
    return this.apiClient.get(`clients/clients/sales-statuses`)
  }
  getClientTypes: () => Promise<ClientType[]> = () => {
    return this.apiClient.get(`clients/clients/types`)
  }
  getClientCompanyTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/company-types`)
  }
  getClientStatus: () => Promise<ClientStatus[]> = () => {
    return this.apiClient.get(`clients/clients/statuses`)
  }
  getStatusReason: () => Promise<StatusReason[]> = () => {
    return this.apiClient.get(`clients/clients/status-reasons`)
  }
  getClientGenderType: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/genders`)
  }
  getClientNciType: () => Promise<NciType[]> = () => {
    return this.apiClient.get(`clients/clients/ncitypes`)
  }
  getClientEducationLevelType: () => Promise<EduLevel[]> = () => {
    return this.apiClient.get(`clients/clients/edulevels`)
  }
  getClientDocumentType: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/doctypes`)
  }
  getKYCProviders: () => Promise<NameDto[]> = () => {
    return this.apiClient.get('clients/clients/kyc-verification-providers')
  }

  getClientOnboardingFlowType: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/countries/onboarding-flow-types`)
  }
  sendClientRequestAdditionalDocument: (clientId: string, documentType: number) => Promise<void> = (
    clientId,
    documentType
  ) => {
    return this.apiClient.post(`clients/clients/${clientId}/request-additional-document`, {
      documentType,
    })
  }
  getClientBusinessNatureType: () => Promise<BusinessNature[]> = () => {
    return this.apiClient.get(`clients/clients/businessnatures`)
  }
  getClientLanguages: () => Promise<Language[]> = () => {
    return this.apiClient.get(`clients/languages`)
  }
  getClientClassificationType: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/classification-types`)
  }
  getClientMarketDataClassificationType: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/clients/market-data-classification`)
  }
  getTruliooFields: (id: string) => Promise<TruliooFieldDto> = (id) => {
    return this.apiClient.get(`/clients/identity-verification/clients/${id}/fields`)
  }
  postToTruliooVerification: (clientId: string, data: TruliooVerification) => Promise<void> = (
    clientId,
    data
  ) => {
    return this.apiClient.post(`/clients/identity-verification/clients/${clientId}/verify`, {
      ...data,
      clientId,
    })
  }
  getClientIdentityVerification: (id: string) => Promise<ClientVerification[]> = (id) => {
    return this.apiClient.get(`/clients/identity-verification/clients/${id}`)
  }
  getClientAMLDetails: (id: string) => Promise<ClientAMLDetails> = (id) => {
    return this.apiClient.get(`/clients/aml/client/${id}/status`)
  }
  getClientAMLDetailed: (id: string) => Promise<AmlValueDetailedDto[]> = (clientId) => {
    return this.apiClient.get(`/clients/aml/client/${clientId}/values-list`)
  }
  getClientLandingPages: (id: string) => Promise<{ landingPages: LandingPageDto[] }[]> = (id) => {
    return this.apiClient.get(`/clients/clients/${id}/landing-pages/options`)
  }

  getCountry2FA: (query?: PageQuery) => Promise<CountryPreference[]> = (query) => {
    return this.apiClient.get(`/clients/countries?${preparePageQueryParams(query)}`)
  }
  getClientIdentityVerificationReportById: (
    id: string
  ) => Promise<ClientVerificationReportDetails> = (id) => {
    return this.apiClient.get(`/clients/identity-verification/report/${id}`)
  }
  getClientNCIDocument: (id: string) => Promise<DocumentTypeDto[]> = (id) => {
    return this.apiClient.get(`/clients/nci/priorities/${id}/document-types`)
  }
  addClientNciHistoryRecords: (data: CreateNciHistory) => Promise<DocumentTypeDto[]> = (data) => {
    return this.apiClient.post(`/clients/nci/client/${data.clientId}/histories`, data)
  }
  addAMLAdjustmentRecord: (data: CreateAMLAdjustment, clientId: string) => Promise<void> = (
    data,
    clientId
  ) => {
    return this.apiClient.put(`/clients/aml/client/${clientId}/adjustment`, data)
  }
  dismissAMLAdjustmentRecord: (adjustmentId: string, clientId: string) => Promise<void> = (
    adjustmentId,
    clientId
  ) => {
    return this.apiClient.put(
      `/clients/aml/client/${clientId}/adjustment/${adjustmentId}/dismiss`,
      {}
    )
  }
  getClientNciPriorities: (
    countryId: string,
    tickmillCompanyId: number
  ) => Promise<NciPriorityDto[]> = (countryId, tickmillCompanyId) => {
    return this.apiClient.get(
      `/clients/nci/priorities?countryId=${countryId}&tickmillCompanyId=${tickmillCompanyId}`
    )
  }
  getClientNCIHistory: (clientId: string) => Promise<NCIHistory[]> = (clientId) => {
    return this.apiClient.get(`/clients/nci/client/${clientId}/histories`)
  }
  getClientRelations: (id: string) => Promise<ClientRelationsDto[]> = (id) => {
    return this.apiClient.get(`clients/clients/${id}/relations`)
  }
  reCalculateClientNCI: (clientId: string) => Promise<DocumentTypeDto[]> = (clientId) => {
    return this.apiClient.post(`/clients/nci/client/${clientId}/recalculate-nci`, {})
  }
  getCountriesPreference: (query?: PageQuery) => Promise<CountryPreferenceDto[]> = (query) => {
    return this.apiClient.get(`/clients/countries?${preparePageQueryParams(query)}`)
  }
  updateCountyPreference: (data: UpdateCountryPreference) => Promise<void> = (data) => {
    return this.apiClient.post(`/clients/countries`, data)
  }
  getCountriesProducts: (query?: PageQuery) => Promise<CountriesProductDto[]> = (query) => {
    return this.apiClient.get(
      `/clients/countries/company-countries?${preparePageQueryParams(query)}`
    )
  }
  updateCountyProduct: (id: string, data: UpdateCountryProduct) => Promise<void> = (id, data) => {
    return this.apiClient.put(`/clients/countries/company-country/${id}`, data)
  }
  getCountryEvents: (countryId: string) => Promise<AuditLog[]> = (countryId) => {
    return this.apiClient.get(`clients/countries/${countryId}/events`)
  }
  getLegalBasesTypes: () => Promise<ClientLegalBasesTypes[]> = () => {
    return this.apiClient.get(`clients/clients/legalbases`)
  }
  getCountryCompliance: (query?: PageQuery) => Promise<PageData<CountryComplianceDto[]>> = (
    query
  ) => {
    return this.apiClient.get(`clients/country-compliancies?${preparePageQueryParams(query)}`)
  }
  getAppropriatenessTestCategories: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/tests/test-categories`)
  }
  getAppropriatenessStatuses: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/tests/test-statuses`)
  }
  updateAppropriatenessTestScoreSettings: (
    testId: string,
    body: AppropriatnessTestScoreSetting
  ) => Promise<void> = (testId, body) => {
    return this.apiClient.post(`clients/tests/edit-score-settings/${testId}`, body)
  }
  getAppropriatenessTestSubCategories: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/tests/test-sub-categories`)
  }
  getAppropriatenessTest: (id: string) => Promise<AppTestDetailsDto> = (testId) => {
    return this.apiClient.get(`clients/tests/new-approach/${testId}`)
  }
  getTestAnswerMessageTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get('clients/tests/answers-message-types')
  }
  getAppropriatenessTests: (query?: PageQuery) => Promise<PageData<AppropriatenessTestDto[]>> = (
    query
  ) => {
    return this.apiClient.get(`clients/tests?${preparePageQueryParams(query)}`)
  }
  getTestbuilderEvents: (testId: string) => Promise<AuditLog[]> = (testId) => {
    return this.apiClient.get(`clients/tests/${testId}/events`)
  }
  updateCountryCompliance: (
    id: string,
    data: AddEditCountryRiskValues
  ) => Promise<CountryComplianceDto[]> = (id, data) => {
    return this.apiClient.put(`clients/country-compliancies/${id}`, data)
  }
  getCountryComplianceEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`clients/country-compliancies/${id}/events`)
  }
  getCountryComplianceMRLOAnalyses: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/country-compliancies/mrlo-analyses`)
  }
  getCountryComplianceInternalRiskLevel: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/country-compliancies/internal-risk-levels`)
  }
  getClientsSecuredPropertiesLabels: () => Promise<SecuredPropertiesLabels[]> = () => {
    return this.apiClient.get(`clients/secured-properties/labels`)
  }
  getRoleClientsSecuredProperties: (query?: PageQuery) => Promise<RoleSecuredProperties[]> = (
    query
  ) => {
    return this.apiClient.get(`clients/secured-properties?${preparePageQueryParams(query)}`)
  }
  setRoleClientsSecuredProperties: (
    data: AddRolePermissionFormValues
  ) => Promise<SecuredPropertiesLabels[]> = (data) => {
    return this.apiClient.post(`clients/secured-properties/set`, data)
  }

  deleteRoleClientsSecuredProperties: (
    data: Omit<AddRolePermissionFormValues, 'securedProperties'> & {
      name: string
    }
  ) => Promise<SecuredPropertiesLabels[]> = ({ aggregateType, role, name }) => {
    return this.apiClient.delete(`clients/secured-properties/${aggregateType}/${role}/${name}`)
  }
  getClientScreeningCases: (query?: PageQuery) => Promise<PageData<ClientScreeningCasesDto[]>> = (
    query
  ) => {
    return this.apiClient.get(`clients/clients/screening-cases?${preparePageQueryParams(query)}`)
  }
  getComplianceLossPercentages: (query?: PageQuery) => Promise<PageData<LossPercentageDto[]>> = (
    query
  ) => {
    return this.apiClient.get(
      `clients/compliance/loss-percentages?${preparePageQueryParams(query)}`
    )
  }
  getComplianceLossPercentageEvents: (lossPercentageId: string) => Promise<AuditLog[]> = (
    lossPercentageId
  ) => {
    return this.apiClient.get(`clients/compliance/loss-percentages/${lossPercentageId}/events`)
  }
  addComplianceLossPercentages: (body: CreateTickmillCompanyLossPercentage) => Promise<void> = (
    body
  ) => {
    return this.apiClient.post(`clients/compliance/loss-percentages`, body)
  }
  updateComplianceLossPercentages: (body: UpdateTickmillCompanyLossPercentage) => Promise<void> = (
    body
  ) => {
    return this.apiClient.put(`clients/compliance/loss-percentages/${body.id}`, body)
  }
  deleteComplianceLossPercentages: (lossPercentageId: string) => Promise<void> = (
    lossPercentageId
  ) => {
    return this.apiClient.delete(`clients/compliance/loss-percentages/${lossPercentageId}`)
  }
  getComplianceVulnerabilityGroups: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`clients/compliance/vulnerability-groups`)
  }
  allowMultiTierRegistration: (
    clientId: string,
    data: UpdateMultiTierRegistrationAllowedFlag
  ) => Promise<void> = (clientId, data) => {
    return this.apiClient.put(`clients/clients/${clientId}/multi-tier-registration-allowed`, data)
  }
  toggleInterestRatesRestriction: (
    clientId: string,
    data: ToggleInterestRatesRestriction
  ) => Promise<void> = (clientId, data) => {
    return this.apiClient.put(`clients/clients/${clientId}/interest-rates`, data)
  }

  toggleRestrictionFromBonusCampaign: (
    clientId: string,
    data: ToggleExcludeFromBonusCampaignRestriction
  ) => Promise<void> = (clientId, data) => {
    return this.apiClient.put(`clients/clients/${clientId}/restriction-from-bonus-campaign`, data)
  }

  toggleLuckyDrawExclusion: (clientId: string, data: ToggleExcludeLuckyDraw) => Promise<void> = (
    clientId,
    data
  ) => {
    return this.apiClient.put(`clients/clients/${clientId}/exclude-from-lucky-draws`, data)
  }

  validateClientPhoneNumber = (clientId: string, phoneNumber: string): Promise<void> => {
    return this.apiClient.get(
      `clients/clients/${clientId}/phone-numbers/lookup?PhoneNumber=${encodeURIComponent(phoneNumber)}`
    )
  }
}
